'use client'

import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import type { DragEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { cn } from '@/util/cn'
import { toolGradientClassName } from '../presentation/gradients'
import { useCreationStore } from '../store/provider'

type Props = {
    tool: CreationTool
    className?: string
    children?: ReactNode
}

/**
 * Legacy structural reference: components/drop-zone.tsx.
 *
 * Legacy tracks dragging in two layers: a window-level listener flips a
 * shared `isDraggingFile` (in `CreationToolsContext`) so every card's CTA
 * flips to "Drop File" together, and a per-card `isDraggedOver` (driven by
 * the DropZone's own `onDragEnter`/`onDragLeave`) paints a diagonal striped
 * gradient border on just the card underneath the cursor.
 *
 * This port attaches the drag handlers directly to the drop zone rather than
 * `window` (simpler, and what task-15-brief.md's test drives), sharing
 * `isDraggingFile` through the creation store (already a store field as of
 * Task 12) so the CTA-flip behavior matches. The striped border-image trick
 * isn't a measured value, so the "something is being dragged over this card"
 * state is reproduced as a plain gradient wash using `toolGradientClassName`
 * instead. `bg-linear-45`, not `bg-gradient-to-br`/`bg-linear-to-br` (135deg)
 * — every legacy creation-tools gradient is `linear-gradient(45deg, ...)`
 * (types/gradient.ts), and this reuses the same colour pairs
 * `welcome/container.tsx` uses for the card's own background wash.
 */
const DropZone = ({ tool, className, children }: Props) => {
    const setIsDraggingFile = useCreationStore((state) => state.setIsDraggingFile)
    const [isDraggedOver, setIsDraggedOver] = useState(false)

    // Legacy's window-level handlers exist for one reason its own comment
    // states: "prevent the browser from opening the file when dropping outside
    // a file input" (components/drop-zone.tsx:81). Attaching only to the zone
    // — as this component otherwise does — leaves the rest of the page
    // unguarded, so releasing a file over the headline or the URL input
    // navigates the tab to its `file://` URL and the app is gone, along with
    // anything the user had entered.
    //
    // A file input is excluded so the picker itself still receives its drop.
    // Every card mounts a DropZone, so several of these listeners can be live
    // at once; preventDefault is idempotent, so that is harmless.
    useEffect(() => {
        const preventNavigation = (e: Event) => {
            if ((e.target as HTMLInputElement)?.type !== 'file') {
                e.preventDefault()
            }
        }

        window.addEventListener('dragover', preventNavigation)
        window.addEventListener('drop', preventNavigation)

        return () => {
            window.removeEventListener('dragover', preventNavigation)
            window.removeEventListener('drop', preventNavigation)
        }
    }, [])

    const onDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDraggingFile(true)
        setIsDraggedOver(true)
    }

    const onDragLeave = () => {
        setIsDraggingFile(false)
        setIsDraggedOver(false)
    }

    const onDrop = () => {
        setIsDraggingFile(false)
        setIsDraggedOver(false)
    }

    return (
        <div
            data-drop-zone
            className={cn('relative rounded-[inherit]', className)}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {isDraggedOver && (
                <div
                    aria-hidden
                    className={cn(
                        'pointer-events-none absolute inset-0 -z-10 rounded-[inherit] bg-linear-45 opacity-40',
                        toolGradientClassName(tool)
                    )}
                />
            )}
            {children}
        </div>
    )
}

export default DropZone
